import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import clsx from 'clsx';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';

import { Dots, LeftArrow, RightArrow } from './controls';

import classes from './carousel-slider.module.scss';

type ControlOptions = 'never' | 'always' | 'hover';

export type CarouselSliderProps = AliceCarousel['props'] & {
  wrapperClassName?: string;
  showControls?: {
    arrows?: ControlOptions;
    dots?: ControlOptions;
  };
  onLeftArrowClick?: () => void;
  onRightArrowClick?: () => void;
  onImageIndexChanged?: (index: number) => Promise<void> | void;
};

export const CarouselSlider = ({
  wrapperClassName,
  showControls = { dots: 'always', arrows: 'always' },
  onLeftArrowClick,
  onRightArrowClick,
  onImageIndexChanged,
  infinite = true,
  items = [],
  ...props
}: CarouselSliderProps) => {
  const { arrows: showArrows, dots: showDots } = showControls;
  const carouselRef = useRef<AliceCarousel | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    /*
      This unfortunately is the only way.
      The carousels onSlideChange returns the step from which it changed.
      Go from 2 to 3, it returns 3, go back to 2 and it also returns 3. Therefore unusable.
      And onSlideChanged gets called after the transition, so the dots would lag behind.
    */
    if (carouselRef.current) {
      carouselRef.current.componentDidUpdate = () => {
        const index = carouselRef.current?.state.activeIndex || 0;

        setActiveIndex((prev) => {
          if (prev !== index && index >= 0 && onImageIndexChanged)
            onImageIndexChanged(index);
          return index;
        });
      };
    }
  }, [onImageIndexChanged]);

  const slidePrev = useCallback((e: any) => {
    if (onLeftArrowClick) onLeftArrowClick();
    carouselRef.current?.slidePrev(e);
  }, []);

  const slideNext = useCallback((e: any) => {
    if (onRightArrowClick) onRightArrowClick();
    carouselRef.current?.slideNext(e);
  }, []);

  const onDotClick = useCallback((slideIndex: number) => {
    carouselRef.current?.slideTo(slideIndex);
  }, []);

  const dots = useMemo(() => {
    if (showDots === 'never' || items.length < 2) return null;
    return (
      <Dots
        className={clsx(
          classes.dots,
          showDots === 'always' && classes.dotsShow,
        )}
        currentIndex={activeIndex}
        itemsLength={items.length}
        onClick={onDotClick}
      />
    );
  }, [activeIndex, items, showDots]);

  const carousel = useMemo(
    () => (
      <AliceCarousel
        {...props}
        items={items}
        ref={carouselRef}
        infinite={infinite}
        disableButtonsControls
        disableDotsControls
      />
    ),
    [infinite, items],
  );

  return (
    <div className={clsx(classes.wrapper, wrapperClassName)}>
      {showArrows !== 'never' && items.length > 1 && (
        <>
          <LeftArrow
            className={clsx(
              classes.arrow,
              classes.left,
              showArrows === 'always' && classes.arrowShow,
            )}
            onClick={slidePrev}
          />
          <RightArrow
            className={clsx(
              classes.arrow,
              classes.right,
              showArrows === 'always' && classes.arrowShow,
            )}
            onClick={slideNext}
          />
        </>
      )}

      {dots}
      {carousel}
    </div>
  );
};
